// Mehmonlar jurnali (№137) — xsib-core `venue` moduli, `GuestJournalController`.
//
// ⚠️ Ro'yxatdagi telefon NIQOBLANGAN (backend `Guest.masked()` — har doim,
// bu servis qatlamining o'zida bajariladi). To'liq ochish alohida va
// AUDIT QILINADIGAN chaqiruv (`revealIdentity`): sabab MAJBURIY va bo'sh
// bo'lmasligi kerak — sababsiz ochish "shunchaki qiziqdim"ni "so'rov
// bo'yicha"dan ajratib bo'lmaydigan qiladi (`GuestJournalController` javadoc).
//
// ⚠️ `from`/`to` HAR DOIM MAJBURIY — `guest_session` cheksiz o'sadigan
// jadval (CLAUDE.md §6).
//
// ⚠️ `revealIdentity` — POST, lekin `reason` TANADA EMAS, QUERY PARAMETRDA
// (backend `@RequestParam String reason`). Bu GET emas ataylab: yon ta'sirli
// chaqiruv (audit yozuvi yaratadi), ya'ni brauzer tarixiga tushmasligi kerak.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import {
  IDENTIFICATION_METHODS,
  IDENTIFICATION_STRENGTHS,
  VENUE_FUNDINGS,
  type IdentificationMethod,
  type IdentificationStrength,
  type VenueFunding,
} from './venueApi'

// --- Guest (mehmon — shaxs, abonent EMAS) -----------------------------------

const guestSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  venueId: uuidSchema,
  /** ⚠️ Maydon nomi `identityMethod` — `GuestSession.identificationMethod` dan farqli */
  identityMethod: z.enum(IDENTIFICATION_METHODS),
  /** Ro'yxatda niqoblangan (`+99890****567`), `reveal` dan keyin to'liq */
  phone: z.string().nullish(),
  /** Ro'yxatda har doim `null` — faqat `reveal` to'ldiradi */
  fullName: z.string().nullish(),
  roomNumber: z.string().nullish(),
  firstSeenAt: instantSchema.nullish(),
  lastSeenAt: instantSchema.nullish(),
  visitCount: z.number(),
  anonymizedAt: instantSchema.nullish(),
})

export interface Guest {
  id: string
  venueId: string
  identityMethod: IdentificationMethod
  phone: string | null
  fullName: string | null
  roomNumber: string | null
  firstSeenAt: string | null
  lastSeenAt: string | null
  visitCount: number
  /** Shaxsiy ma'lumot saqlash muddati o'tib anonimlashtirilgan bo'lsa */
  anonymizedAt: string | null
}

function toGuest(raw: z.infer<typeof guestSchema>): Guest {
  return {
    id: raw.id,
    venueId: raw.venueId,
    identityMethod: raw.identityMethod,
    phone: raw.phone ?? null,
    fullName: raw.fullName ?? null,
    roomNumber: raw.roomNumber ?? null,
    firstSeenAt: raw.firstSeenAt ?? null,
    lastSeenAt: raw.lastSeenAt ?? null,
    visitCount: raw.visitCount,
    anonymizedAt: raw.anonymizedAt ?? null,
  }
}

// --- GuestSession (bitta TASHRIF — radacct sessiyasi EMAS) ------------------

const guestSessionSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  venueId: uuidSchema,
  zoneId: uuidSchema,
  guestId: uuidSchema,
  mac: z.string().nullish(),
  ip: z.string().nullish(),
  identificationMethod: z.enum(IDENTIFICATION_METHODS),
  identificationStrength: z.enum(IDENTIFICATION_STRENGTHS),
  startedAt: instantSchema,
  expiresAt: instantSchema,
  endedAt: instantSchema.nullish(),
  endReason: z.string().nullish(),
  subscriberLogin: z.string().nullish(),
  funding: z.enum(VENUE_FUNDINGS),
  campaignId: uuidSchema.nullish(),
})

export interface GuestSession {
  id: string
  venueId: string
  zoneId: string
  mac: string | null
  ip: string | null
  identificationMethod: IdentificationMethod
  identificationStrength: IdentificationStrength
  startedAt: string
  expiresAt: string
  endedAt: string | null
  endReason: string | null
  subscriberLogin: string | null
  funding: VenueFunding
  campaignId: string | null
}

function toSession(raw: z.infer<typeof guestSessionSchema>): GuestSession {
  return {
    id: raw.id,
    venueId: raw.venueId,
    zoneId: raw.zoneId,
    mac: raw.mac ?? null,
    ip: raw.ip ?? null,
    identificationMethod: raw.identificationMethod,
    identificationStrength: raw.identificationStrength,
    startedAt: raw.startedAt,
    expiresAt: raw.expiresAt,
    endedAt: raw.endedAt ?? null,
    endReason: raw.endReason ?? null,
    subscriberLogin: raw.subscriberLogin ?? null,
    funding: raw.funding,
    campaignId: raw.campaignId ?? null,
  }
}

// --- Jurnal sahifasi ---------------------------------------------------------

const journalEntrySchema = z.object({
  session: guestSessionSchema,
  guest: guestSchema,
  venueName: z.string(),
  zoneName: z.string(),
})

export interface JournalEntry {
  session: GuestSession
  guest: Guest
  venueName: string
  zoneName: string
}

function toEntry(raw: z.infer<typeof journalEntrySchema>): JournalEntry {
  return {
    session: toSession(raw.session),
    guest: toGuest(raw.guest),
    venueName: raw.venueName,
    zoneName: raw.zoneName,
  }
}

const journalPageSchema = z.object({
  rows: z.array(journalEntrySchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export interface JournalFilter {
  /** ISO-8601 UTC — MAJBURIY */
  from: string
  to: string
  venueId?: string | null
  zoneId?: string | null
  phone?: string | null
  mac?: string | null
  login?: string | null
  onlyInsufficient?: boolean
}

/**
 * Jurnal sahifasini oladi. Filtr YOPILGAN qiymat sifatida beriladi
 * (`VenueJournalPage` sahifasi filtr o'zgarganda `useMemo` bilan qayta
 * yaratadi — kesh kaliti ham filtrga bog'liq, `AuditPage` bilan bir xil andoza).
 */
export function fetchJournalPage(
  filter: JournalFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<JournalEntry>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/venues/journal', {
      signal,
      params: {
        from: filter.from,
        to: filter.to,
        ...(filter.venueId ? { venueId: filter.venueId } : {}),
        ...(filter.zoneId ? { zoneId: filter.zoneId } : {}),
        ...(filter.phone ? { phone: filter.phone } : {}),
        ...(filter.mac ? { mac: filter.mac } : {}),
        ...(filter.login ? { login: filter.login } : {}),
        onlyInsufficient: filter.onlyInsufficient ?? false,
        // Backend sahifani 0 dan sanaydi — boshqa ro'yxat endpointlari bilan bir xil andoza
        page: state.page - 1,
        size: state.pageSize,
      },
    })
    const parsed = journalPageSchema.parse(response.data)
    return {
      items: parsed.rows.map(toEntry),
      total: parsed.total,
    }
  }
}

export async function fetchJournalEntry(sessionId: string): Promise<JournalEntry> {
  const response = await api.get<unknown>(`/api/venues/journal/${sessionId}`)
  return toEntry(journalEntrySchema.parse(response.data))
}

/**
 * To'liq shaxsiy ma'lumotni ochadi — HAR CHAQIRUV AUDIT'GA YOZILADI.
 *
 * ⚠️ `reason` majburiy va bo'sh bo'lmasligi kerak — chaqiruvchi buni
 * tekshirishi shart, backend ham qat'iy talab qiladi.
 */
export async function revealIdentity(sessionId: string, reason: string): Promise<Guest> {
  const response = await api.post<unknown>(`/api/venues/journal/${sessionId}/identity`, undefined, {
    params: { reason },
  })
  return toGuest(guestSchema.parse(response.data))
}

const complianceSchema = z.object({
  venueId: uuidSchema,
  from: instantSchema,
  to: instantSchema,
  totalSessions: z.number(),
  sufficientSessions: z.number(),
  delegatedSessions: z.number(),
  insufficientSessions: z.number(),
  uniqueGuests: z.number(),
})

export interface ComplianceSummary {
  totalSessions: number
  sufficientSessions: number
  delegatedSessions: number
  insufficientSessions: number
  uniqueGuests: number
  /** Yetarli identifikatsiya ulushi, foizda — backend hisoblamaydi, shu yerda */
  compliantPercent: number
}

/** Ob'ekt bo'yicha rioya ko'rsatkichi — №137 uchun eng muhim raqam */
export async function fetchCompliance(
  venueId: string,
  from: string,
  to: string,
): Promise<ComplianceSummary> {
  const response = await api.get<unknown>('/api/venues/journal/compliance', {
    params: { venueId, from, to },
  })
  const parsed = complianceSchema.parse(response.data)
  const compliantPercent =
    parsed.totalSessions === 0
      ? 100
      : ((parsed.sufficientSessions + parsed.delegatedSessions) * 100) / parsed.totalSessions
  return {
    totalSessions: parsed.totalSessions,
    sufficientSessions: parsed.sufficientSessions,
    delegatedSessions: parsed.delegatedSessions,
    insufficientSessions: parsed.insufficientSessions,
    uniqueGuests: parsed.uniqueGuests,
    compliantPercent,
  }
}
